// Vendored from the JETCAM DXF Viewer (src/render/overlay.ts), 2026-09-04.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Unmodified. The ../geometry and ../parse imports resolve to the shims
// beside this directory, which re-export the engine.

import { type Primitive, primitiveLength } from "../geometry/index.ts";
import { type LayerRecord } from "../parse/index.ts";
import { type Camera } from "./camera.ts";
import { resolveColorIndex, resolveLinetype, type Rgb, rgbToCss } from "./colors.ts";

/**
 * Colour names for the tooltip's "colour N — name" line.
 *
 * Not the AutoCAD Color Index: this is the reseller's own numbering for the
 * colours that actually turn up in JETCAM output, given directly rather than
 * derived from `aciToRgb`'s ACI table, which uses different indices for the
 * same names. Indices with no entry here show the number alone.
 */
const COLOUR_NAMES: Readonly<Record<number, string>> = {
  250: "Black",
  7: "White",
  2: "Yellow",
  6: "Light Magenta",
  1: "Light Red",
  4: "Light Cyan",
  3: "Light Green",
  5: "Light Blue",
  8: "Dark Grey",
  9: "Light Grey",
  32: "Brown",
  212: "Dark Magenta",
  12: "Dark Red",
  132: "Dark Cyan",
  92: "Dark Green",
  172: "Dark Blue",
};

/** The tooltip's "colour ..." line: a resolved index with its name when known, or a note when there is no index to show. */
function describeColor(p: Primitive, layers: ReadonlyMap<string, LayerRecord>): string {
  const result = resolveColorIndex(p.color, p.layer, layers, p.trueColor);
  if (result.kind === "trueColor") return "colour (true colour)";
  if (result.kind === "unresolved") return "colour (layer not found)";
  const name = COLOUR_NAMES[result.index];
  return name !== undefined ? `colour ${result.index} — ${name}` : `colour ${result.index}`;
}

/**
 * Screen-space overlays: the hover tooltip.
 *
 * Drawn on the canvas rather than as a floating DOM node. In embedded mode the
 * host page's stylesheet owns everything outside our canvas, and a positioned
 * tooltip is exactly the sort of element a host's CSS reset mangles. Painting
 * it ourselves also avoids a DOM write on every mouse move.
 */

/** Round to a sensible number of significant digits for a readout. */
function formatNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs === 0) return "0";
  if (abs >= 1000) return value.toFixed(1);
  if (abs >= 1) return value.toFixed(3).replace(/\.?0+$/, "");
  return value.toPrecision(4).replace(/\.?0+$/, "");
}

/**
 * What the tooltip says about a primitive: what it is, where it lives, and the
 * one measurement a person actually wants — a radius for an arc, a length for
 * anything drawn along a path.
 */
export function describePrimitive(p: Primitive, layers: ReadonlyMap<string, LayerRecord>): string[] {
  const lines: string[] = [];
  // The source format's own type name, not our primitive kind: a reseller
  // looking at the file wants to hear "LWPOLYLINE", not "polyline".
  lines.push(p.ref.sourceType);
  lines.push(`layer ${p.layer}`);
  lines.push(`linetype ${resolveLinetype(p.linetype, p.layer, layers)}`);
  lines.push(describeColor(p, layers));

  switch (p.kind) {
    case "arc":
      lines.push(`radius ${formatNumber(p.radius)}`);
      lines.push(`length ${formatNumber(primitiveLength(p))}`);
      break;
    case "line":
    case "polyline":
      lines.push(`length ${formatNumber(primitiveLength(p))}`);
      break;
    case "point":
      lines.push(`at ${formatNumber(p.position.x)}, ${formatNumber(p.position.y)}`);
      break;
    case "text":
      lines.push(p.text.length > 40 ? `${p.text.slice(0, 40)}...` : p.text);
      break;
  }

  if (p.blockPath.length > 0) lines.push(`in ${p.blockPath.join(" > ")}`);
  if (p.ref.handle !== undefined) lines.push(`handle ${p.ref.handle}`);
  return lines;
}

export interface TooltipStyle {
  readonly background: Rgb;
  readonly border: Rgb;
  readonly text: Rgb;
  readonly fontFamily: string;
  readonly fontSizePx: number;
}

export const DARK_TOOLTIP: TooltipStyle = {
  background: { r: 20, g: 20, b: 20 },
  border: { r: 110, g: 110, b: 110 },
  text: { r: 235, g: 235, b: 235 },
  fontFamily: 'ui-sans-serif, system-ui, "Segoe UI", Arial, sans-serif',
  fontSizePx: 11,
};

export const LIGHT_TOOLTIP: TooltipStyle = {
  ...DARK_TOOLTIP,
  background: { r: 255, g: 255, b: 255 },
  border: { r: 150, g: 150, b: 150 },
  text: { r: 25, g: 25, b: 25 },
};

export function drawTooltip(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
  style: TooltipStyle,
  devicePixelRatio: number,
  lines: readonly string[],
  cursor: { x: number; y: number },
): void {
  if (lines.length === 0) return;

  ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
  ctx.font = `${style.fontSizePx}px ${style.fontFamily}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";

  const padding = 6;
  const lineHeight = style.fontSizePx + 3;
  const width = Math.max(...lines.map((l) => ctx.measureText(l).width)) + padding * 2;
  const height = lines.length * lineHeight + padding * 2;

  // Flip to the other side of the cursor rather than running off the canvas.
  let x = cursor.x + 14;
  let y = cursor.y + 14;
  if (x + width > camera.width) x = Math.max(0, cursor.x - width - 8);
  if (y + height > camera.height) y = Math.max(0, cursor.y - height - 8);

  ctx.fillStyle = rgbToCss(style.background);
  ctx.fillRect(x, y, width, height);
  ctx.strokeStyle = rgbToCss(style.border);
  ctx.lineWidth = 1;
  ctx.strokeRect(x + 0.5, y + 0.5, width, height);

  ctx.fillStyle = rgbToCss(style.text);
  lines.forEach((line, i) => {
    ctx.fillText(line, x + padding, y + padding + i * lineHeight);
  });
}
